import type { CSSProperties, KeyboardEvent } from 'react';
import { useRef } from 'react';
import { useTheme, type ThemeMode } from '@/theme/ThemeProvider';
import { Icon, type IconName } from './Icon';

const MODES: { value: ThemeMode; label: string; icon: IconName }[] = [
  { value: 'light', label: 'Светлая', icon: 'sun' },
  { value: 'dark', label: 'Тёмная', icon: 'moon' },
  { value: 'system', label: 'Система', icon: 'monitor' },
];

/**
 * Three-way theme switch.
 *
 * A radio group, keyboard included: Tab reaches the current option, the
 * arrow keys move between options and select as they go (WAI-ARIA radio
 * pattern). In the glass skin the selection is one sliding thumb
 * (`.rt-seg::before`) rather than three backgrounds switching on and off;
 * its position is the `--rt-seg-index` custom property set below.
 */
export function ThemeToggle() {
  const { mode, setMode } = useTheme();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const activeIndex = Math.max(
    0,
    MODES.findIndex((option) => option.value === mode),
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (activeIndex + step + MODES.length) % MODES.length;
    setMode(MODES[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="Тема оформления"
      className="rt-seg"
      onKeyDown={onKeyDown}
      style={{ '--rt-seg-index': activeIndex, '--rt-seg-count': MODES.length } as CSSProperties}
    >
      {MODES.map((option, index) => {
        const active = index === activeIndex;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={option.label}
            title={option.label}
            tabIndex={active ? 0 : -1}
            className="rt-seg__item"
            onClick={() => setMode(option.value)}
          >
            <Icon name={option.icon} size={15} />
          </button>
        );
      })}
    </div>
  );
}
